import type { FileUrlInfo } from '../utils/fileSharingUrlUtils';

import { GatekeeperCustomError } from '../utils/ErrorHandler';
import { TelemetryTrackEvent } from '../utils/Telemetry';
import { fetchUrlInfo, getQueryParam } from '../utils/fileSharingUrlUtils';
import { trackFileHoster } from '../utils/telemetry/matomo';

import { errorMessageStore } from './ErrorMessageStore';

import { GatekeeperError } from '@egr/gatekeeper-lib/lib/errors';
import { EaiwsSessionManager } from '@egr/xbox/app-api/EaiwsSessionManager';
import { withProgress } from '@egr/xbox/progress-manager/ProgressManager';
import { setCustomDimension } from '@egr/xbox/telemetry/MatomoUtils';
import { getEnvVar } from '@egr/xbox/utils/ReactScriptHelper';
import { SyncExternalStore } from '@egr/xbox/utils/SyncExternalStore';

const APPLICATION_ID = getEnvVar('APPLICATION_ID')!;
export const GATEKEEPER_ID = getEnvVar('GATEKEEPER_ID')!;

interface AppStoreState {
    embed: boolean;
    wizard: boolean;
    faq: boolean;
    urlInfo: FileUrlInfo | undefined;
}

export const SessionManager = new EaiwsSessionManager(APPLICATION_ID, (error: unknown) => {
    if (error instanceof GatekeeperError) {
        errorMessageStore.open(new GatekeeperCustomError(error));
    }
});

class AppStore extends SyncExternalStore<AppStoreState> {
    private localObjectUrl: string | null = null;

    constructor() {
        super({
            embed: getQueryParam('embed') !== undefined,
            wizard: false,
            faq: false,
            urlInfo: undefined,
        });
    }
    public setwizard(wizard: boolean) {
        if (this.internalState.wizard != wizard) {
            this.updateState({ wizard });
        }
    }

    public getEmbed(): boolean {
        return this.snapshot.embed;
    }

    public setEmbed(embed: boolean) {
        if (this.internalState.embed != embed) {
            this.updateState({ embed });
        }
    }

    public getFaq(): boolean {
        return this.snapshot.faq;
    }

    public setFaq(faq: boolean) {
        if (this.internalState.faq != faq) {
            this.updateState({ faq });
        }
    }

    @withProgress('fetch urlInfo')
    public async fetchUrlInfo() {
        const urlInfo = await fetchUrlInfo();
        this.updateState({
            urlInfo,
        });

        if (urlInfo?.fileUrl != null) {
            trackFileHoster(urlInfo?.fileUrl);
            setCustomDimension(2, urlInfo.extension);

            if ((window.location.href.includes('glb=') && window.location.href.includes('usdz=')) || urlInfo.arFile) {
                TelemetryTrackEvent('Load content', 'glb_usdz', '1');
            } else {
                TelemetryTrackEvent('Load content', urlInfo.extension, '1');
            }
        }

        if (urlInfo?.extension === 'pbox' || urlInfo?.extension === 'pec') {
            void SessionManager.initializeSession(GATEKEEPER_ID);
        }
    }

    public getShareLink(urlInfo: FileUrlInfo, embed?: boolean): string {
        const currentUrl = new URL(window.location.href);
        const params = new URLSearchParams(currentUrl.search);

        const embedUrl = urlInfo.fileUrl;
        if (embed) {
            params.set('embed', encodeURIComponent(embedUrl));
            params.delete('url');
        } else {
            params.set('url', encodeURIComponent(embedUrl));
        }

        const shareableUrl = `${currentUrl.origin}${currentUrl.pathname}?${params.toString()}`;

        return shareableUrl;
    }

    public async setFileUrl(url: string) {
        window.history.pushState('', '', '?url=' + encodeURIComponent(url));
    }

    public async setLocalFile(file: File): Promise<void> {
        const extension = file.name.split('.').pop()?.toLowerCase() as FileUrlInfo['extension'] | undefined;
        if (extension == null || extension === '') {
            throw new Error('Unsupported file extension');
        }

        if (this.localObjectUrl != null) {
            URL.revokeObjectURL(this.localObjectUrl);
        }
        this.localObjectUrl = URL.createObjectURL(file);

        const urlInfo: FileUrlInfo = {
            fileUrl: file.name,
            fileDownloadUrl: this.localObjectUrl,
            extension,
            arFile: false,
        };

        this.updateState({
            urlInfo,
            embed: false,
        });

        if (urlInfo.extension === 'pbox' || urlInfo.extension === 'pec') {
            await SessionManager.initializeSession(GATEKEEPER_ID);
        }
    }
}

export const appStore = new AppStore();
